import { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { getMe, loginUser, logoutUser, verifyMfaCode } from "../services/userService";

// ── Verrou d'inactivité ──────────────────────────────────────────────────
// Corrigé le 15/09/2026 : ce minuteur déconnectait TOUJOURS après 8h sans
// activité, même sur une session "Se souvenir de moi" (30 jours) — ce qui
// rendait cette option inopérante en pratique (ex: connecté le soir, le
// minuteur se déclenche pendant la nuit, reconnexion exigée le matin même
// si "Se souvenir de moi" était coché). Le choix de l'utilisateur est
// maintenant mémorisé dans localStorage et resetInactivityTimer() ignore
// le minuteur tant qu'il est actif : seule l'expiration réelle du token
// (30 jours) fait alors foi, gérée côté serveur + l'événement
// "auth:session-expired" ci-dessous.
const INACTIVITY_LIMIT = 8 * 60 * 60 * 1000; // 8 heures
const ACTIVITY_EVENTS  = ["mousemove", "keydown", "click", "scroll", "touchstart"];
const REMEMBER_ME_KEY  = "rememberMe";

const AuthContext = createContext({
  user:       null,
  loading:    true,
  login:      async () => {},
  logout:     async () => {},
  verifyMfa:  async () => {},
  can:        () => false,
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {

  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimer = useRef(null);

  const logout = useCallback(async () => {
    clearTimeout(inactivityTimer.current);
    localStorage.removeItem(REMEMBER_ME_KEY);
    await logoutUser();
    setUser(null);
  }, []);

  const resetInactivityTimer = useCallback(() => {
    clearTimeout(inactivityTimer.current);
    // "Se souvenir de moi" coché → pas de déconnexion pour inactivité.
    if (localStorage.getItem(REMEMBER_ME_KEY) === "true") return;
    inactivityTimer.current = setTimeout(() => logout(), INACTIVITY_LIMIT);
  }, [logout]);

  const startInactivityWatch = useCallback(() => {
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, resetInactivityTimer));
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const stopInactivityWatch = useCallback(() => {
    ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, resetInactivityTimer));
    clearTimeout(inactivityTimer.current);
  }, [resetInactivityTimer]);

  useEffect(() => {
    const handleExpired = () => {
      clearTimeout(inactivityTimer.current);
      stopInactivityWatch();
      localStorage.removeItem(REMEMBER_ME_KEY);
      setUser(null);
    };
    window.addEventListener("auth:session-expired", handleExpired);
    return () => window.removeEventListener("auth:session-expired", handleExpired);
  }, [stopInactivityWatch]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setLoading(false); return; }

    getMe()
      .then(profileRes => {
        setUser(profileRes.data);
        startInactivityWatch();
      })
      .catch(() => {
        localStorage.removeItem("token");
        setUser(null);
      })
      .finally(() => setLoading(false));

    return () => stopInactivityWatch();
  }, []);

  // Étape 1 — Email + mot de passe
  const login = async (credentials) => {
    const res = await loginUser(credentials);
    if (res.requiresMfa) return res;
    // Pas de MFA sur ce compte : on ne connaît pas le choix "Se souvenir de
    // moi" ici (coché uniquement sur l'écran MFA) — on part sur false par
    // défaut plutôt que de laisser traîner une valeur d'une session précédente.
    localStorage.setItem(REMEMBER_ME_KEY, "false");
    const profileRes = await getMe();
    setUser(profileRes.data);
    startInactivityWatch();
    return res;
  };

  // Étape 2 — Vérifier le code MFA
  const verifyMfa = async (code, rememberMe = false) => {
    await verifyMfaCode(code, rememberMe);
    localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? "true" : "false");
    const profileRes = await getMe();
    setUser(profileRes.data);
    startInactivityWatch();
  };

  /**
   * Vérification de permission — portail ADMIN uniquement.
   *
   * Contrairement au portail client, l'admin n'a pas de permissions
   * granulaires par fonctionnalité côté frontend : seul un compte
   * SUPER_ADMIN peut se connecter ici, la vraie sécurité étant déjà
   * assurée côté backend via @PreAuthorize("hasRole('SUPER_ADMIN')") sur
   * chaque endpoint /admin/*. `can()` existe uniquement pour que du code
   * partagé avec le portail client (où les permissions sont réelles et
   * par utilisateur) puisse l'appeler sans planter — elle retourne
   * toujours true tant qu'un utilisateur est connecté, exactement comme
   * MyPasswordsPage.jsx le fait déjà en dur pour les mots de passe
   * ("Admin : accès total — même comportement que le portail client").
   */
  const can = useCallback(() => !!user, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, verifyMfa, can }}>
      {children}
    </AuthContext.Provider>
  );
}